import { useCallback, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';

import {
  Checkbox,
  Text,
  TextField,
  colors,
} from '@soluto-private/mx-asurion-ui-react';

import {
  useAnalytics,
  useInViewAnalytics,
} from '@service-initiation/shared-data-access-analytics';
import {
  useGoToNextStep,
  useGoToStep,
} from '@service-initiation/shared-data-access-flow-manager';
import { ClickableText } from '@service-initiation/shared-ui-clickable-text';
import { NoAvailabilityModal } from '@service-initiation/shared-ui-no-availability-modal';
import {
  LeftMainContentSection,
  MainContentContainer,
  PageContainer,
} from '@service-initiation/shared-ui-page-layout';
import { StepContentSkeleton } from '@service-initiation/shared-ui-step-content-skeleton';
import { TranslationEntries } from '@service-initiation/tech-care-data-access-i18n';
import { Route } from '@service-initiation/tech-care-data-access-routing';
import {
  useBillingDetailsAtom,
  useResetSelectedAppointment,
  useSetAlertBannerAtom,
} from '@service-initiation/tech-care-data-access-store';
import { ElementsProvider } from '@service-initiation/tech-care-data-access-stripe';
import {
  ErrorType,
  invalidateCachedServiceOptions,
} from '@service-initiation/tech-care-data-access-tech-care-services';
import {
  privacyPolicyUrl,
  termsOfServiceUrl,
} from '@service-initiation/tech-care-ui-legal-links';
import {
  NavigationButtonsContainer,
  type NavigationButtonsContainerProps,
} from '@service-initiation/tech-care-ui-navigation-buttons';
import { StepTitle } from '@service-initiation/tech-care-ui-step-title';

import {
  BillingAddressForm,
  PaymentForm,
  PaymentRow,
  StripeSection,
} from './components';
import { useConfirmWindowAvailability, useCreatePaymentIntent } from './hooks';
import { billingDetailsFormSchema } from './schemas';
import { defaultStripeElementErrors } from './types';

const Container = styled.div``;

const AddressCheckbox = styled(Checkbox)`
  margin-top: 33px;
  margin-bottom: 32px;
`;

const handleLinkClick = (link?: string) => {
  window.open(link, '_blank');
};

const TEXT = TranslationEntries.Repairs.BillingDetails;
const ERROR_TEXT = TranslationEntries.Repairs.Error;
const TIME_TAKEN_ERROR = TranslationEntries.Repairs.TimeTakenError;

const BillingDetails = () => {
  const { t } = useTranslation();
  const [billingDetails, setBillingDetails] = useBillingDetailsAtom();

  const resetSelectedAppointment = useResetSelectedAppointment();
  const [, goToNextStep] = useGoToNextStep();
  const { goToStep } = useGoToStep();
  const setAlertBanner = useSetAlertBannerAtom();
  const { dispatchClickEvent } = useAnalytics();
  const { ref } = useInViewAnalytics('BillingDetailsPage');
  const {
    error: errorConfirmWindowAvailability,
    loading: loadingConfirmWindowAvailability,
  } = useConfirmWindowAvailability();
  const [
    { error: errorCreatePaymentIntent, loading: loadingCreatePaymentIntent },
    createPaymentIntent,
  ] = useCreatePaymentIntent();

  const [ccErrors, setCCErrors] = useState({
    CCCVC: defaultStripeElementErrors.CCCVC,
    CCExp: defaultStripeElementErrors.CCExp,
    CCNumber: defaultStripeElementErrors.CCNumber,
  });

  const [errorFields, setErrorFields] = useState({
    firstName: '',
    lastName: '',
  });

  const validateForm = ({
    fieldName,
    fieldValue,
  }: {
    fieldName: string;
    fieldValue: unknown;
  }) => {
    const fieldData = { [fieldName]: fieldValue };

    billingDetailsFormSchema(t)
      .validateAt(fieldName, fieldData)
      .then(() => {
        setErrorFields({ ...errorFields, ...{ [fieldName]: '' } });
      })
      .catch((error) => {
        setErrorFields({
          ...errorFields,
          ...{ [fieldName]: error?.errors[0] },
        });
      });
  };

  const handleBlur = ({
    target: { checked, name, value },
  }: React.BaseSyntheticEvent<Partial<HTMLInputElement>>) => {
    const fieldValue = name === 'billingIsSame' ? checked : value;
    validateForm({ fieldName: name, fieldValue: fieldValue });
  };

  const navigateButtonProps: NavigationButtonsContainerProps = {
    disableContinueButton: !billingDetailsFormSchema().isValidSync({
      ...billingDetails?.billingAddress,
      billingIsSame: billingDetails?.billingIsSame,
      firstName: billingDetails?.firstName,
      lastName: billingDetails?.lastName,
      stripeElementErrors: ccErrors,
    }),
    isLoading: loadingCreatePaymentIntent,
    onContinueButtonClick: createPaymentIntent,
    pageName: 'BillingDetails',
    showBackButton: true,
  };

  const reselectAppointmentTime = useCallback(
    (cta: string) => {
      invalidateCachedServiceOptions();
      resetSelectedAppointment();
      goToStep(Route.AppointmentTime);
      dispatchClickEvent('GoToTimeSlot', {
        Cta: cta,
        ErrorType: 'SlotTaken',
      });
    },
    [dispatchClickEvent, goToStep, resetSelectedAppointment]
  );

  useEffect(() => {
    if (errorCreatePaymentIntent) {
      setAlertBanner({
        bannerType: 'error',
        message: t(ERROR_TEXT.GenericError),
      });
    }
    if (!errorCreatePaymentIntent && billingDetails?.payment_intent_id) {
      goToNextStep();
    }
  }, [
    billingDetails?.payment_intent_id,
    errorCreatePaymentIntent,
    goToNextStep,
    goToStep,
    setAlertBanner,
    t,
  ]);

  return errorConfirmWindowAvailability?.message ===
    ErrorType.SlotIsTakenError ? (
    <NoAvailabilityModal
      dataTestId="no-availability-modal"
      isOpen={true}
      onButtonClick={() =>
        reselectAppointmentTime(t(TIME_TAKEN_ERROR.Button.Text))
      }
      onCloseClick={() => reselectAppointmentTime('CloseX')}
      primaryButtonLabel={t(TIME_TAKEN_ERROR.Button.Text)}
      subtitle={t(TIME_TAKEN_ERROR.Text)}
      title={t(TIME_TAKEN_ERROR.Title)}
    />
  ) : (
    <PageContainer>
      <MainContentContainer>
        <LeftMainContentSection>
          {loadingConfirmWindowAvailability ? (
            <StepContentSkeleton />
          ) : (
            <Container data-testid="billing-details-root" ref={ref}>
              <StepTitle subtitle={t(TEXT.Subtitle)} title={t(TEXT.Title)} />
              <PaymentForm>
                <StripeSection ccErrors={ccErrors} setErrors={setCCErrors} />
                <PaymentRow>
                  <TextField
                    autoComplete="cc-given-name"
                    className="fs-mask"
                    data-testid="billing-details-first-name"
                    fieldStatus={errorFields['firstName'] ? 'error' : 'default'}
                    helperText={errorFields['firstName']}
                    label={t(TEXT.FirstNameLabel)}
                    name="firstName"
                    onBlur={handleBlur}
                    onChange={(e) => {
                      setBillingDetails({
                        ...billingDetails,
                        firstName: e.target.value,
                      });
                    }}
                    value={billingDetails?.firstName}
                  />
                  <TextField
                    autoComplete="cc-family-name"
                    className="fs-mask"
                    data-testid="billing-details-last-name"
                    fieldStatus={errorFields['lastName'] ? 'error' : 'default'}
                    helperText={errorFields['lastName']}
                    label={t(TEXT.LastNameLabel)}
                    name="lastName"
                    onBlur={handleBlur}
                    onChange={(e) => {
                      setBillingDetails({
                        ...billingDetails,
                        lastName: e.target.value,
                      });
                    }}
                    value={billingDetails?.lastName}
                  />
                </PaymentRow>
              </PaymentForm>
              <AddressCheckbox
                checked={billingDetails?.billingIsSame}
                label={t(TEXT.IsBillingSameText)}
                name="billingIsSame"
                onBlur={handleBlur}
                onChange={() => {
                  setBillingDetails({
                    ...billingDetails,
                    billingIsSame: !billingDetails?.billingIsSame,
                  });
                }}
              />
              {!billingDetails?.billingIsSame && <BillingAddressForm />}
              <div>
                <Trans
                  components={{
                    Text: <Text />,
                    pp: (
                      <ClickableText
                        color={colors.primaryDeep}
                        onClick={() => handleLinkClick(privacyPolicyUrl)}
                      />
                    ),
                    tos: (
                      <ClickableText
                        color={colors.primaryDeep}
                        onClick={() => handleLinkClick(termsOfServiceUrl)}
                      />
                    ),
                  }}
                  i18nKey={TEXT.TermsAndPrivacyLabel}
                />
              </div>
            </Container>
          )}
        </LeftMainContentSection>
        <NavigationButtonsContainer {...navigateButtonProps} />
      </MainContentContainer>
    </PageContainer>
  );
};

export const BillingDetailsPage = () => (
  <ElementsProvider>
    <BillingDetails />
  </ElementsProvider>
);
